import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useMutation } from '@tanstack/react-query';
import { ptBR } from 'date-fns/locale';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiArrowFromBottom,BiArrowFromTop,BiPlus,BiTrash } from 'react-icons/bi';
import { FaEye } from "react-icons/fa";
import { toast } from 'react-toastify';
import AttToken from '../../../helpers/attToken';
import getEndOfToday from '../../../helpers/getEndOfToday';
import { trackEventMatomo } from '../../../helpers/matomo';
import PlanoDeAcao,{ planoDeAcaoType as LegacyPlanoDeAcaoType } from '../../../service/PlanoDeAcao';
import queryClient from '../../../service/query';
import { logPlanoType,planoDeAcaoType,taskType } from '../../../types/plano-de-acao/types';
import Loading from '../../Loading/Loading';
import UserSelector from '../../UserSelector';
import { SpanBelongs } from '../style/stylesPlano';
import Logs from './Logs';

type props = {
  plano: planoDeAcaoType | null,
  setPlano: Function,
  restarting: boolean,
  openModal: boolean,
  handleOpen: Function
};

type FormPaProps = {
  plano: planoDeAcaoType,
  setPlano: Function,
  restarting: boolean,
};

type TaskCompProps = {
  index: number,
  task: taskType
  setPlano: Function,
  plano: planoDeAcaoType,
  restarting: boolean
};

const planoService = new PlanoDeAcao();

const TaskComp = ({ task, index, setPlano, plano, restarting }: TaskCompProps) => {
  const { t } = useTranslation('translation');
  const handleAddTaskResponsible = (uid: string) => {
    task.responsavel = uid;
    setPlano({ ...plano });
  };

  return (
    <Card sx={{ width: '100%' }}>
      <Stack
        spacing={2}
        direction='column'
        width='100%'
        justifyContent='flex-start'
        alignItems='flex-start'
        padding={2}
        className={task.status === 'aprovado' || (task.status !== 'pendente' && restarting) ? 'tasksDisabled' : 'tasks'}>
        {task.status === 'aprovado' || (task.status !== 'pendente' && restarting) ? (
          <Typography fontWeight={600} color='textDisabled'>{task.nome}</Typography>
        ) : (
          <TextField
            fullWidth
            label='Nome da tarefa:'
            value={task.nome}
            onChange={(e) => {
              task.nome = e.target.value;
              setPlano({ ...plano });
            }}
            required
          />
        )}
        <Stack direction='column' alignItems='flex-start' justifyContent='flex-start' width='100%' spacing={2}>
          <Typography color='textDisabled' fontWeight={600}>{t('ActionPlan.Modal.responsible')}</Typography>
          {task.status !== 'pendente' ? (
            <Typography color='textDisabled' fontWeight={600}>{task.responsavel}</Typography>
          ) : (
            <UserSelector
              callback={handleAddTaskResponsible}
              userLabel={t('ActionPlan.Modal.selectTaskResponsible')}
              defaultSelected={task.responsavel || null}
            />
          )}
        </Stack>
        {task.status !== 'pendente' ? (
          <Typography>{t('ActionPlan.Modal.performTaskBy')} {new Date(task.dataLimite).toLocaleDateString('pt-BR')}</Typography>
        ) : (
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <DesktopDatePicker
              label={t('ActionPlan.Modal.performTaskBy')}
              format='dd/MM/yyyy'
              value={task.dataLimite ? new Date(task.dataLimite) : new Date()}
              maxDate={plano.dataLimite ? new Date(plano.dataLimite) : undefined}
              onChange={(newValue) => {
                if (newValue) {
                  task.dataLimite = newValue.getTime();
                  setPlano({ ...plano });
                }
              }
              }
              slotProps={{
                field: {
                  readOnly: true,
                },
              }}
              minDate={getEndOfToday().endDate}
            />
          </LocalizationProvider>
        )}
        {task.status === 'aprovado' || (task.status !== 'pendente' && restarting) ? (
          <Typography color='textDisabled' fontWeight={600}>{`${task.obrigatorioEvidencia ? `${t('ActionPlan.Modal.yes')}` : `${t('ActionPlan.Modal.no')}`}`}</Typography>
        ) : (
          <FormControlLabel control={<Checkbox checked={task.obrigatorioEvidencia}
            onChange={(e) => {
              task.obrigatorioEvidencia = e.target.checked;
              setPlano({ ...plano });
            }} />} label={t('ActionPlan.Modal.evidenceRequired')} />
        )}
        {task.status === 'aprovado' || (task.status !== 'pendente' && restarting) ? (
          <>
            <Typography>Detalhes da Tarefa:</Typography>
            <Typography>{task.detalhes || `${t('ActionPlan.Modal.noDetailsProvided')}`}</Typography>
          </>
        ) : (
          <TextField
            multiline
            fullWidth
            rows={4}
            maxRows={4}
            slotProps={{
              htmlInput: {
                maxLength: 1000,
              },
            }}
            onChange={(e) => {
              task.detalhes = e.target.value;
              setPlano({ ...plano });
            }}
            label={t('ActionPlan.Modal.detailTaskOptional')}
          />
        )}
        {task.status === 'aprovado' && (
          <span className='tag is-success'>{t('ActionPlan.Modal.taskAlreadyCompleted')}</span>
        )}
        {task.status === 'pendente' && (
          <IconButton
            color='error'
            disabled={plano.tasks.length <= 1}
            onClick={() => {
              const newTasks = plano.tasks.filter((el, ind) => ind !== index);
              plano.tasks = newTasks;
              setPlano({ ...plano });
            }}
          >
            <BiTrash />
          </IconButton>
        )}
      </Stack>
    </Card>
  );
};

const FormPA = ({ plano, setPlano, restarting }: FormPaProps,) => {
  const [inverteOrdem, setInverteOrdem] = useState(true);
  const { t } = useTranslation('translation');
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState<logPlanoType[]>([]);

  function handleOpen() {
    setOpen(!open);
  };

  const addTask = () => {
    const defaultTask: taskType = {
      nome: 'Nova Tarefa',
      status: 'pendente',
      responsavel: '',
      obrigatorioEvidencia: false,
      detalhes: '',
      dataLimite: getEndOfToday().endDate.getTime(),
    };
    if (!plano.tasks || !plano.tasks.length) {
      plano.tasks = [defaultTask];
    } else { plano.tasks.push(defaultTask); }
    setPlano({ ...plano });
  };

  return (
    <>
      {open && <Logs openLogs={open} handleOpenLogs={handleOpen} logs={logs} />}
      <Paper sx={{ padding: 2 }}>
        <Typography variant='h5' fontWeight={600} textAlign='center' sx={{ color: '#54afa2' }}>{t('ActionPlan.Modal.planOptions')}</Typography>
        <TextField
          onClick={() => trackEventMatomo('modal edita PA', 'click', 'input', 'edita valor')}
          label={t('ActionPlan.Modal.actionPlanName')}
          fullWidth
          type="text"
          value={plano.nome}
          onChange={(e) => {
            plano.nome = e.target.value;
            setPlano({ ...plano });
          }}
          required

        />
        <Typography fontWeight={600} color='textDisabled' fontSize='small'>{t('ActionPlan.Modal.actionPlanResponsible')} {plano.responsavel}</Typography>
        <Typography fontWeight={600} color='textDisabled' fontSize='small'>{t('ActionPlan.Modal.finishPlanBy')} {new Date(plano.dataLimite).toLocaleDateString('pt-BR')}</Typography>
        <Typography variant='h5' fontWeight={600} textAlign='center' sx={{ color: '#54afa2' }}>{t('ActionPlan.Modal.nonConformities')}</Typography>
        {plano.naoConformidades.map((sub, index) => (
          <>
            <Typography key={index} style={{ wordBreak: 'break-all', maxWidth: '95%' }}>{index + 1} - {sub.nome}</Typography>
            {sub.avaliacao?.map((ava, ind) => (
              <Typography key={ind} paddingLeft={1} variant='body2' fontWeight={`${sub.nota === ava.valor ? 600 : 'none'}`}>{ava.nome}</Typography>))}
          </>
        ))}
        <Typography variant='h5' fontWeight={600} textAlign='center' sx={{ color: '#54afa2' }}>Histórico:</Typography>
        {plano.tasks.map((task) => (
          <Stack direction='column' justifyContent='flex-start' alignItems='flex-start' width='100%' spacing={1}>
            <Stack direction='row' justifyContent='flex-start' alignItems='center' spacing={1.5} width='100%'>
              Tarefa: {task.nome}
              <Tooltip title={task.log
                ? `${t('ActionPlan.PlanosDeAcaoComp.viewLogs')}`
                : `${t('ActionPlan.PlanosDeAcaoComp.noLogsAvailable')}`}>
                <Button endIcon={task.log?.length ? <FaEye /> : <></>} disabled={!task.log?.length} onClick={() => {
                  setLogs(task?.log as logPlanoType[]);
                  setOpen(true);
                }}>
                  {task.log
                    ? `${t('ActionPlan.PlanosDeAcaoComp.viewLogs')}`
                    : `${t('ActionPlan.PlanosDeAcaoComp.noLogsAvailable')}`}
                </Button>
              </Tooltip>
            </Stack>
            Responsável: {task.responsavel}
            <Divider variant='middle' flexItem />
          </Stack>
        ))}
        <Typography variant='h5' fontWeight={600} textAlign='center' sx={{ color: '#54afa2' }}>{t('ActionPlan.Modal.defineTasksForThisPlan')}</Typography>
        <Stack direction='row' justifyContent='space-around' alignItems='center' width='100%'>
          <Tooltip title={t('ActionPlan.Modal.addTask')}>
            <Button
              endIcon={<BiPlus />}
              color='success'
              onClick={() => {
                trackEventMatomo('modal edita PA', 'click', 'button', 'salva');
                addTask();
              }}
            >
              {t('ActionPlan.Modal.addTask')}
            </Button>
          </Tooltip>
          <Tooltip title={`${t('ActionPlan.Modal.viewTasks')} ${inverteOrdem
            ? `${t('ActionPlan.Modal.oldestFirst')}`
            : `${t('ActionPlan.Modal.newestFirst')}`} ${t('ActionPlan.Modal.creationOrder')}`} style={{ width: '25%' }}>
            <Button
              endIcon={inverteOrdem ? <BiArrowFromBottom /> : <BiArrowFromTop />}
              color='info'
              onClick={() => {
                trackEventMatomo('modal edita PA', 'click', 'button', 'altera ordem');
                setInverteOrdem(!inverteOrdem);
              }}
            >
              {`Tarefas ${inverteOrdem
                ? `${t('ActionPlan.Modal.oldestFirst')}`
                : `${t('ActionPlan.Modal.newestFirst')}`}`}
            </Button>
          </Tooltip>
        </Stack>
        {(!plano.tasks || !plano.tasks.length) && <SpanBelongs>{t('ActionPlan.Modal.noTasksCreatedYet')}</SpanBelongs>}
        <Stack direction={inverteOrdem ? 'column-reverse' : 'column'} justifyContent='flex-start' alignItems='flex-start' spacing={1} width='100%'>
          {plano.tasks && plano.tasks.map((task, index) => (
            <TaskComp
              key={index}
              task={task}
              index={index}
              plano={plano}
              setPlano={setPlano}
              restarting={restarting}
            />
          ))}
        </Stack>
      </Paper>
    </>
  );
};

export default function EditaPlano({
  plano,
  setPlano,
  restarting,
  openModal,
  handleOpen,
}: props) {
  const { t } = useTranslation('translation');

  const { mutate, isPending } = useMutation({
    mutationKey: ['EDITING_PLAN'],
    mutationFn: async () => {
      toast.info(t('ActionPlan.Modal.editingPlan'), { toastId: 'enviando' });
      const token = await AttToken();
      if (token) {
        const res = await planoService.editPlan(token, plano as unknown as LegacyPlanoDeAcaoType, restarting);
        return res;

      }
    },
    onSuccess: () => {
      setPlano(null);
      toast.success(t('ActionPlan.Modal.planEdited'), { toastId: 'success' });
      if (restarting) {
        queryClient.resetQueries({ queryKey: ['GET_ANALISES_PENDING'] });
        queryClient.resetQueries({ queryKey: ['GET_ANALISES_FINALIZADAS'] });
        queryClient.resetQueries({ queryKey: ['GET_MALSUCEDIDOS'] });
      } else {
        queryClient.resetQueries({ queryKey: ['GET_PLANOS_ACAO'] });
      }
    },
    onError: () => {
      toast.error(t('ActionPlan.Modal.error'), { toastId: 'errro' });
    }
  });

  return (
    <>
      <Dialog
        fullWidth
        maxWidth='md'
        open={openModal}
        onClose={() => handleOpen()}
        scroll='paper'
      >
        <DialogTitle>{`${t('ActionPlan.Modal.editing')} ${plano?.nome}`}</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => handleOpen()}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers={true}>
          {!isPending ? (
            <FormPA
              plano={plano as planoDeAcaoType}
              setPlano={setPlano}
              restarting={restarting}
            />
          ) : (
            <div>
              <h1>{t('ActionPlan.Modal.editingPlan2')}</h1>
              <Loading />
            </div>
          )}
        </DialogContent >
        <DialogActions sx={{ padding: 2 }}>
          {plano?.tasks.every(task => task.status === 'aprovado') ? (
            <span className='tag is-warning'>{t('ActionPlan.Modal.addAtLeastOneNewTask')}</span>
          ) : (
            <Button
              onClick={(e) => {
                trackEventMatomo('modal edita PA', 'click', 'form', 'salva PA');
                mutate();
              }}
              color='success'
              variant='contained'
              disabled={!plano?.tasks || !plano?.tasks.length}
              type='submit'
            >
              {`${t('ActionPlan.Modal.finishEditing')} ${plano?.nome}`}
            </Button>
          )}
        </DialogActions>
      </Dialog >
    </>
  );
}
